import { zodResolver } from '@hookform/resolvers/zod';
import { type QuoteDocument, type QuoteLine } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTrigger,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Textarea,
  toast,
} from '@kalos/ui';
import { ImageIcon } from '@radix-ui/react-icons';
import { getLineDiscount } from 'kalos-modules';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useMediaQuery } from '../../../hooks/useMedia';
import { MemoClientService } from '../../../tools/helpers';
import {
  computeApprovedProposalTotal,
  proposalPricingFromQuoteDocument,
} from '../../../utils/proposalApprovedTotal';
import {
  getQuoteDocumentTaxCost,
  getQuoteDocumentTaxRate,
} from '../../../utils/quoteDocumentPricing';
import { ProposalInvoicePhotoSelector } from '../../ComponentsLibrary/ProposalInvoicePhotoSelector';

// Schema for client information
const clientInfoSchema = z.object({
  displayName: z.string().optional(),
  notes: z.string().optional(),
  withJobNotes: z.boolean().default(false),
  includeAttachments: z.boolean().default(true),
  preparedById: z.number().optional(),
  displayBusinessName: z.boolean().default(false),
  validUntilDays: z.number().default(7), // Number of days until proposal expires, default 7
});

type ClientInfoFormData = z.infer<typeof clientInfoSchema>;

interface Props {
  selectedServices: QuoteLine[];
  onClientInfoChange: (clientInfo: ClientInfoFormData & { blockLineItems?: boolean }) => void;
  clientInfo: {
    displayName?: string;
    notes?: string;
    withJobNotes: boolean;
    includeAttachments?: boolean;
    preparedById?: number;
    displayBusinessName?: boolean;
    validUntilDays: number;
    blockLineItems?: boolean;
  };
  serviceCallId?: string; // Job number for the service call
  quoteDocument: QuoteDocument | null;
  customer: {
    firstname?: string;
    lastname?: string;
    businessname?: string;
    email?: string;
    phone?: string;
  };
  property?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  loggedUserId: number;
  serviceItem?: {
    discount?: string;
    discountcost?: string;
  };
  onPhotosBusyChange?: (isBusy: boolean) => void;
}

// Helper function to validate date strings and provide a fallback
const isValidDate = (dateString: string) => {
  // Explicitly check for MySQL NULL date formats
  if (
    !dateString ||
    dateString === '0000-00-00 00:00:00' ||
    dateString === '0000-00-00' ||
    dateString === '0001-01-01 00:00:00'
  ) {
    return false;
  }

  const date = new Date(dateString);
  // Check if the date is valid and not from the distant past
  return !isNaN(date.getTime()) && date.getFullYear() > 1970;
};

export const ReviewStep: FC<Props> = ({
  selectedServices,
  onClientInfoChange,
  clientInfo,
  serviceCallId,
  quoteDocument,
  customer,
  property,
  loggedUserId,
  serviceItem,
  onPhotosBusyChange,
}) => {
  const [showNotes, setShowNotes] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(false);
  const [isPhotosDialogOpen, setIsPhotosDialogOpen] = useState(false);

  // Use media query to determine if we're on mobile
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Ref for the debounce timeout
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate total price based on quantity and adjustment - consistent with ServicesStep
  const calculateTotalPrice = (service: QuoteLine): number => {
    const quantity = service.quotedQuantity || 1;
    const price = parseFloat(service.adjustment || '0');
    return price * quantity;
  };

  // Form for client information and notes
  const form = useForm<ClientInfoFormData>({
    resolver: zodResolver(clientInfoSchema),
    defaultValues: {
      displayName: clientInfo.displayName,
      notes: clientInfo.notes || quoteDocument?.jobNotes || '',
      withJobNotes: clientInfo.withJobNotes || false,
      includeAttachments: true, // Always true now
      preparedById: clientInfo.preparedById,
      displayBusinessName: clientInfo.displayBusinessName ?? false,
      validUntilDays: clientInfo.validUntilDays,
    },
  });

  // Watch the displayBusinessName value to update displayName
  const displayBusinessName = form.watch('displayBusinessName');

  // Calculate the display name based on the checkbox and availability of business name
  const displayName =
    displayBusinessName && customer?.businessname && customer.businessname.trim() !== ''
      ? customer.businessname
      : `${customer?.firstname || ''} ${customer?.lastname || ''}`.trim();

  // Update the display name when displayBusinessName changes
  useEffect(() => {
    form.setValue('displayName', displayName);
  }, [displayBusinessName, displayName, form]);

  // Calculate days between quote creation and expiration dates
  // This handles existing quotes where the expiration was already set
  const calculateQuoteDays = useCallback(() => {
    if (quoteDocument?.quoteExpireDate && isValidDate(quoteDocument.quoteExpireDate)) {
      const expireDate = new Date(quoteDocument.quoteExpireDate);

      // If we have a creation date, calculate from creation to expire
      if (quoteDocument?.createdDate && isValidDate(quoteDocument.createdDate)) {
        const createdDate = new Date(quoteDocument.createdDate);

        // Calculate the difference in days from creation date to expiration
        const diffTime = expireDate.getTime() - createdDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays > 0 ? diffDays : 7; // Default to 7 if calculation is invalid
      } else {
        // If we don't have a valid creation date, calculate from today to expiration
        const today = new Date();
        const diffTime = expireDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays > 0 ? diffDays : 7; // Default to 7 if expiration is in the past
      }
    }
    return null; // No calculation possible for quotes without expiration date
  }, [quoteDocument?.quoteExpireDate, quoteDocument?.createdDate]);

  // Update valid until days when quote document changes
  useEffect(() => {
    const calculatedDays = calculateQuoteDays();
    if (calculatedDays !== null) {
      form.setValue('validUntilDays', calculatedDays);
    }
  }, [calculateQuoteDays, form]);

  // Function to handle form data processing and submission
  const handleFormData = useCallback(
    (data: ClientInfoFormData, showToast = false) => {
      // Calculate display name using the same logic as the displayName variable
      const calculatedDisplayName =
        data.displayBusinessName && customer?.businessname && customer.businessname.trim() !== ''
          ? customer.businessname
          : `${customer?.firstname || ''} ${customer?.lastname || ''}`.trim();

      // Include the calculated display name in the form data
      const updatedData = {
        ...data,
        displayName: calculatedDisplayName,
        // If quote document has expire date, use the calculated days
        validUntilDays: quoteDocument?.quoteExpireDate
          ? calculateQuoteDays() || data.validUntilDays
          : data.validUntilDays,
        // Always set these values
        includeAttachments: true,
        // Preserve blockLineItems from parent clientInfo
        blockLineItems: clientInfo.blockLineItems,
      };

      onClientInfoChange(updatedData);

      // Only show toast if explicitly requested
      if (showToast) {
        toast({
          title: 'Information saved',
          variant: 'success',
        });
      }
    },
    [calculateQuoteDays, onClientInfoChange, customer, quoteDocument, clientInfo.blockLineItems],
  );

  // We need a separate function for the form submission handler
  const onSubmit = useCallback(
    (data: ClientInfoFormData) => {
      handleFormData(data);
    },
    [handleFormData],
  );

  // Debounce function for form auto-save using useRef to maintain timeout state
  const debouncedSubmit = useCallback(
    (data: ClientInfoFormData) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        handleFormData(data);
        timeoutRef.current = null;
      }, 1000); // 1 second debounce
    },
    [handleFormData],
  );

  // Auto-save when form fields change
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      // Skip initial render
      if (!name) return;

      // Don't auto-save for displayName since it's derived
      if (name === 'displayName') return;

      // Don't auto-save for includeAttachments and withJobNotes
      if (name === 'includeAttachments' || name === 'withJobNotes') return;

      // Submit the form with current values
      const currentData = form.getValues();
      debouncedSubmit(currentData);
    });

    return () => subscription.unsubscribe();
  }, [debouncedSubmit, form]);

  // Calculate totals - use calculateTotalPrice for consistency with ServicesStep
  const subtotal = selectedServices.reduce((sum, service) => {
    return sum + calculateTotalPrice(service);
  }, 0);

  const discountPercent = parseFloat(
    quoteDocument?.id ? quoteDocument.discount || '0' : serviceItem?.discount || '0',
  );
  const discountCostValue = parseFloat(
    quoteDocument?.id ? quoteDocument.discountcost || '0' : serviceItem?.discountcost || '0',
  );
  const discountAmount =
    discountPercent > 0
      ? selectedServices.reduce(
          (sum, service) =>
            sum +
            getLineDiscount(
              calculateTotalPrice(service),
              discountPercent,
              discountCostValue,
              service.excludeInvoiceDiscount,
            ),
          0,
        )
      : discountCostValue;
  const afterDiscount = subtotal - discountAmount;
  const taxRate = quoteDocument?.id ? getQuoteDocumentTaxRate(quoteDocument) : 0;
  const taxCostFlat = quoteDocument?.id ? getQuoteDocumentTaxCost(quoteDocument) : 0;
  const taxAmount = taxRate > 0 ? afterDiscount * (taxRate / 100) : taxCostFlat;
  const total = afterDiscount + taxAmount;

  const isSigned = isValidDate(quoteDocument?.signedDate ?? '');

  const approvedBreakdown = useMemo(
    () =>
      computeApprovedProposalTotal(
        selectedServices,
        proposalPricingFromQuoteDocument(quoteDocument),
      ),
    [selectedServices, quoteDocument],
  );

  // Toggle job notes visibility
  const toggleJobNotes = () => {
    setShowNotes(!showNotes);
  };

  // Mobile-friendly service list
  const renderMobileServiceList = () => {
    return (
      <div className="space-y-3">
        {selectedServices.map((service) => {
          const quantity = service.quotedQuantity || 1;
          const unitPrice = parseFloat(service.adjustment || '0');
          const totalPrice = calculateTotalPrice(service);
          const lineDiscount = getLineDiscount(
            totalPrice,
            discountPercent,
            discountCostValue,
            service.excludeInvoiceDiscount,
          );

          return (
            <div key={service.id} className="rounded border bg-white p-3">
              <div className="mb-2">
                <p className="hyphens-auto break-words text-sm font-medium text-gray-900">
                  {service.description}
                </p>
                {service.diagnosis && (
                  <p className="mt-1 hyphens-auto break-words text-xs text-gray-500">
                    {service.diagnosis}
                  </p>
                )}
              </div>

              <div className="border-t pt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Qty: {quantity}</span>
                    <span className="text-xs text-gray-500">×</span>
                    <span className="text-xs text-gray-500">${unitPrice.toFixed(2)}</span>
                    <span className="text-xs text-gray-500">=</span>
                    <span className="whitespace-nowrap text-sm font-medium">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                  {lineDiscount > 0 && (
                    <span className="whitespace-nowrap text-sm font-medium text-green-700">
                      Discount -${lineDiscount.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Totals Section */}
        {isSigned ? (
          <div className="space-y-2 rounded border-t-2 bg-gray-50 p-3">
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>Proposed Subtotal:</span>
              <span>${approvedBreakdown.proposedSubtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>Declined:</span>
              <span className="text-red-400">-${approvedBreakdown.declinedAmount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm font-bold">
              <span>Approved Subtotal:</span>
              <span>${approvedBreakdown.approvedSubtotal.toFixed(2)}</span>
            </div>
            {approvedBreakdown.discountOnApproved > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Discount:</span>
                <span className="text-red-600">
                  -${approvedBreakdown.discountOnApproved.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between border-t pt-2 font-bold">
              <span>Approved Total:</span>
              <span>${approvedBreakdown.approvedTotal.toFixed(2)}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-2 rounded border-t-2 bg-gray-50 p-3">
            <div className="flex items-center justify-between text-sm">
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {(discountPercent > 0 || discountCostValue > 0) && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{discountPercent > 0 ? 'Total discount:' : 'Discount:'}</span>
                <span>-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            {(taxRate > 0 || taxCostFlat > 0) && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{taxRate > 0 ? `Tax (${taxRate}%):` : 'Tax (flat):'}</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t pt-2 font-bold">
              <span>Total:</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Standard desktop table view
  const renderDesktopServiceTable = () => {
    return (
      <div className="overflow-hidden rounded-lg border-2 border-gray-400">
        {/* Responsive table that fits on screen without horizontal scrolling */}
        <table className="w-full table-fixed">
          <colgroup>
            <col style={{ width: '50%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 text-xs font-medium text-gray-700">
              <th className="border-b-2 border-r-2 border-gray-400 p-2 text-left">
                Service Description
              </th>
              <th className="border-b-2 border-r-2 border-gray-400 p-2 text-center">Qty</th>
              <th className="border-b-2 border-r-2 border-gray-400 p-2 text-right">Unit Price</th>
              <th className="border-b-2 border-gray-400 p-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {selectedServices.map((service) => {
              const quantity = service.quotedQuantity || 1;
              const unitPrice = parseFloat(service.adjustment || '0');
              const totalPrice = calculateTotalPrice(service);
              const lineDiscount = getLineDiscount(
                totalPrice,
                discountPercent,
                discountCostValue,
                service.excludeInvoiceDiscount,
              );

              return (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="border-r-2 border-gray-400 p-2 align-top">
                    <div className="max-w-full overflow-hidden">
                      <p
                        className="hyphens-auto break-words text-sm font-medium leading-tight text-gray-900"
                        title={service.description}
                      >
                        {service.description}
                      </p>
                      {service.diagnosis && (
                        <p className="mt-1 text-xs leading-tight text-gray-500">
                          {service.diagnosis}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="border-r-2 border-gray-400 p-2 text-center align-top">
                    <span className="text-sm">{quantity}</span>
                  </td>
                  <td className="border-r-2 border-gray-400 p-2 text-right align-top">
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm">
                      ${unitPrice.toFixed(2)}
                    </span>
                  </td>
                  <td className="p-2 text-right align-top">
                    <span className="whitespace-nowrap text-sm font-medium">
                      ${totalPrice.toFixed(2)}
                    </span>
                    {lineDiscount > 0 && (
                      <span className="block whitespace-nowrap text-sm font-medium text-green-700">
                        Discount -${lineDiscount.toFixed(2)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {/* Totals Rows */}
            {isSigned ? (
              <>
                <tr className="border-t-2 border-gray-400 bg-gray-50 text-gray-400">
                  <td className="border-r-2 border-gray-400 p-2 text-right text-sm" colSpan={3}>
                    Proposed Subtotal:
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-sm">
                      ${approvedBreakdown.proposedSubtotal.toFixed(2)}
                    </span>
                  </td>
                </tr>
                <tr className="bg-gray-50 text-gray-400">
                  <td className="border-r-2 border-gray-400 p-2 text-right text-sm" colSpan={3}>
                    Declined:
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-sm text-red-400">
                      -${approvedBreakdown.declinedAmount.toFixed(2)}
                    </span>
                  </td>
                </tr>
                <tr className="bg-gray-50 font-bold">
                  <td className="border-r-2 border-gray-400 p-2 text-right text-sm" colSpan={3}>
                    Approved Subtotal:
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-sm font-bold">
                      ${approvedBreakdown.approvedSubtotal.toFixed(2)}
                    </span>
                  </td>
                </tr>
                {approvedBreakdown.discountOnApproved > 0 && (
                  <tr className="bg-gray-50">
                    <td
                      className="border-r-2 border-gray-400 p-2 text-right text-sm text-gray-600"
                      colSpan={3}
                    >
                      Discount:
                    </td>
                    <td className="p-2 text-right">
                      <span className="text-sm text-red-600">
                        -${approvedBreakdown.discountOnApproved.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                )}
                <tr className="border-t bg-gray-50 font-bold">
                  <td className="border-r-2 border-gray-400 p-2 text-right text-sm" colSpan={3}>
                    Approved Total:
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-base font-bold">
                      ${approvedBreakdown.approvedTotal.toFixed(2)}
                    </span>
                  </td>
                </tr>
              </>
            ) : (
              <>
                <tr className="border-t-2 border-gray-400 bg-gray-50">
                  <td className="border-r-2 border-gray-400 p-2 text-right text-sm" colSpan={3}>
                    Subtotal:
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-sm font-medium">${subtotal.toFixed(2)}</span>
                  </td>
                </tr>
                {(discountPercent > 0 || discountCostValue > 0) && (
                  <tr className="bg-gray-50">
                    <td
                      className="border-r-2 border-gray-400 p-2 text-right text-sm text-gray-600"
                      colSpan={3}
                    >
                      {discountPercent > 0 ? 'Total discount:' : 'Discount:'}
                    </td>
                    <td className="p-2 text-right">
                      <span className="text-sm text-gray-600">-${discountAmount.toFixed(2)}</span>
                    </td>
                  </tr>
                )}
                {(taxRate > 0 || taxCostFlat > 0) && (
                  <tr className="bg-gray-50">
                    <td
                      className="border-r-2 border-gray-400 p-2 text-right text-sm text-gray-600"
                      colSpan={3}
                    >
                      {taxRate > 0 ? `Tax (${taxRate}%):` : 'Tax (flat):'}
                    </td>
                    <td className="p-2 text-right">
                      <span className="text-sm text-gray-600">${taxAmount.toFixed(2)}</span>
                    </td>
                  </tr>
                )}
                <tr className="border-t bg-gray-50 font-bold">
                  <td className="border-r-2 border-gray-400 p-2 text-right text-sm" colSpan={3}>
                    Total:
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-base font-bold">${total.toFixed(2)}</span>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Client Information Section */}
      <Card className="bg-white p-3">
        <h2 className="mb-2 text-lg font-semibold">Client Information</h2>

        <FormContext {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            {/* Display Name Section */}
            <div className="space-y-3">
              <div>
                <FormLabel className="text-sm">Display Name</FormLabel>
                <div className="flex items-center justify-between">
                  <input
                    className="flex h-9 w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm text-gray-700 opacity-80"
                    readOnly
                    value={displayName}
                    disabled
                  />
                </div>
              </div>

              <div className="mt-2 grid grid-cols-12 gap-x-4 gap-y-3 border-t pt-3">
                <div className="col-span-4">
                  <h3 className="mb-1 text-sm text-gray-500">Client Name</h3>
                  <p className="truncate text-sm font-medium">
                    {customer?.firstname || ''} {customer?.lastname || ''}
                  </p>
                </div>

                <div className="col-span-4">
                  <h3 className="mb-1 text-sm text-gray-500">Company</h3>
                  <p className="truncate text-sm font-medium">{customer?.businessname || ''}</p>
                </div>

                <div className="col-span-4">
                  <h3 className="mb-1 text-sm text-gray-500">Email</h3>
                  <p className="truncate text-sm font-medium">{customer?.email || ''}</p>
                </div>

                <div className="col-span-4">
                  <h3 className="mb-1 text-sm text-gray-500">Phone</h3>
                  <p className="truncate text-sm font-medium">{customer?.phone || ''}</p>
                </div>

                <div className="col-span-8">
                  <h3 className="mb-1 text-sm text-gray-500">Address</h3>
                  <p className="text-sm font-medium">
                    {property?.address || ''}
                    {property?.city && property?.state ? (
                      <>
                        <br />
                        {property.city}, {property.state} {property?.zip || ''}
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
            </div>
          </form>
        </FormContext>
      </Card>

      {/* Job Notes Section - Now as a separate card */}
      <Card className="bg-white p-3">
        <h2 className="mb-2 text-lg font-semibold">Job/Technician Notes</h2>

        <FormContext {...form}>
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="bg-white" onClick={toggleJobNotes}>
                {showNotes ? 'Hide Job Notes' : 'Show Job Notes'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={async () => {
                  if (serviceCallId) {
                    setIsLoading(true);
                    try {
                      // Use the MemoClientService directly to get the job summary
                      const request = {
                        jobIds: [parseInt(serviceCallId, 10)],
                      };
                      const response = await MemoClientService.GetJobSummary(request);

                      // Get the latest summary if available
                      if (
                        response &&
                        response.jobMemoSummaries &&
                        response.jobMemoSummaries.length > 0
                      ) {
                        const latestSummary =
                          response.jobMemoSummaries[response.jobMemoSummaries.length - 1].summary;
                        if (latestSummary) {
                          // Update the form with the AI summary
                          form.setValue('notes', latestSummary);
                          // Save the form data
                          const currentData = form.getValues();
                          handleFormData(currentData);
                        }
                      }
                    } catch (error) {
                      console.error('Error fetching job summary:', error);
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
              >
                Pull AI Summary
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={() => {
                  const currentData = form.getValues();
                  handleFormData(currentData, true);
                }}
              >
                Save Notes
              </Button>
            </div>
          </div>

          {/* Job Notes - Only visible when showNotes is true */}
          {showNotes && (
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder="Enter job notes..."
                      className={`border-input ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring resize-none bg-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                        expandedNotes ? 'h-96' : 'h-20'
                      }`}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);

                        // Manual debounced save - more reliable than the form.watch effect
                        if (timeoutRef.current) {
                          clearTimeout(timeoutRef.current);
                        }

                        timeoutRef.current = setTimeout(() => {
                          const currentData = form.getValues();
                          handleFormData(currentData);
                          timeoutRef.current = null;
                        }, 1000);
                      }}
                    />
                  </FormControl>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => setExpandedNotes(!expandedNotes)}
                    >
                      {expandedNotes ? 'Collapse' : 'Expand'} notes field
                    </Button>
                  </div>
                </FormItem>
              )}
            />
          )}

          {/* Supporting Photos - inside notes section */}
          {quoteDocument?.id && (
            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <div>
                <h4 className="font-medium">Supporting Photos</h4>
              </div>
              <Dialog open={isPhotosDialogOpen} onOpenChange={setIsPhotosDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Add Photos
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex h-[90vh] max-h-[90vh] w-[95vw] max-w-4xl flex-col overflow-hidden rounded-lg border bg-white p-0 shadow-lg sm:h-auto sm:max-h-[90vh] sm:w-auto">
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <ProposalInvoicePhotoSelector
                      eventId={serviceCallId ? parseInt(serviceCallId, 10) : 0}
                      filter={{ quoteDocumentId: quoteDocument.id }}
                      bucket="quote-photo"
                      label="Supporting Evidence"
                      loggedUserId={loggedUserId}
                      onBusyStateChange={onPhotosBusyChange}
                    />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </FormContext>
      </Card>

      {/* Services with file uploads */}
      <Card className="bg-white p-3">
        <div className="mb-2">
          <h2 className="text-lg font-semibold">Services & Documentation</h2>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-6">
            <div className="mb-2 flex items-center justify-center">
              <svg
                className="animate-spin text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 20 20"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="8.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeOpacity=".2"
                />
                <circle
                  cx="10"
                  cy="10"
                  r="8.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="13.3525 40.0575"
                />
              </svg>
            </div>
            <div className="text-gray-500">Loading service documentation...</div>
          </div>
        ) : selectedServices.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            No services added to this proposal yet. Add services from the Services tab.
          </p>
        ) : (
          <div className="space-y-3">
            {/* Use different layouts for mobile vs desktop */}
            {isMobile ? renderMobileServiceList() : renderDesktopServiceTable()}
          </div>
        )}
      </Card>
    </div>
  );
};
